<template>
  <Dialog
    v-model:visible="showUpdatingDialog"
    :showHeader="false"
    :closeOnEscape="false"
    :closable="false"
    :draggable="false"
    :style="{ width: '50vw' }"
    :modal="true"
  >
    <Loading :title="'Updating ' + entityName" :full="false" class="mt-5" />
  </Dialog>
  <Dialog
    v-model:visible="showUpdatePhoneDialog"
    modal
    :draggable="false"
    header="Update Phone number"
    class="modern-dialog"
    :style="{ width: '28rem' }"
  >
    <template #header>
      <div class="inline-flex align-items-center justify-content-center gap-3">
        <i class="fa fa-phone text-yellow-500 text-xl"></i>
        <h3 class="font-semibold white-space-nowrap m-0 p-0 text-gray-800">
          Update Phone Number
        </h3>
      </div>
    </template>
    <div class="text-center p-4">
      <p class="text-gray-700 mb-2">
        You are about to update the users phone number to
        <span class="font-bold"
          >"+{{ user.phoneCountryCode }} {{ user.phone }}"</span
        >.
      </p>
      <p class="text-gray-600">This will have an effect on the users login.</p>
      <div class="flex gap-3 justify-content-center mt-4">
        <Button
          class="px-6 py-2 border-round-lg"
          severity="secondary"
          outlined
          @click="() => (showUpdatePhoneDialog = false)"
          :label="'Cancel'"
        />
        <Button
          severity="warning"
          class="px-6 py-2 border-round-lg font-semibold"
          @click="updatePhone"
          :label="'Accept Update'"
        />
      </div>
    </div>
  </Dialog>
  <Dialog
    v-model:visible="showUpdateEmailDialog"
    modal
    :draggable="false"
    header="Update Email"
    class="modern-dialog"
    :style="{ width: '28rem' }"
  >
    <template #header>
      <div class="inline-flex align-items-center justify-content-center gap-3">
        <i class="fa fa-envelope text-blue-500 text-xl"></i>
        <h3 class="font-semibold white-space-nowrap m-0 p-0 text-gray-800">
          Update Email
        </h3>
      </div>
    </template>
    <div class="text-center p-4">
      <p class="text-gray-700 mb-2">
        You are about to update the users email to
        <span class="font-bold">"{{ user.email }}"</span>.
      </p>
      <p class="text-gray-600">This can have an effect on the users login.</p>
      <div class="flex gap-3 justify-content-center mt-4">
        <Button
          class="px-6 py-2 border-round-lg"
          severity="secondary"
          outlined
          @click="() => (showUpdateEmailDialog = false)"
          :label="'Cancel'"
        />
        <Button
          severity="info"
          class="px-6 py-2 border-round-lg font-semibold"
          @click="updateEmail"
          :label="'Accept'"
        />
      </div>
    </div>
  </Dialog>

  <!-- Award Points Dialog -->
  <Dialog
    v-model:visible="showAwardPointsDialog"
    modal
    :draggable="false"
    header="Award Points Manually"
    :style="{ width: '450px' }"
  >
    <div class="award-points-dialog">
      <div class="mb-4">
        <p class="m-0 text-600 mb-3">
          Award points manually to {{ user?.firstName }} {{ user?.lastName }}
        </p>

        <div class="field mb-4">
          <label for="pointsAmount" class="block text-900 font-medium mb-2">
            Points Amount <span class="text-red-500">*</span>
          </label>
          <InputNumber
            id="pointsAmount"
            v-model="awardPointsForm.points"
            :min="1"
            placeholder="Enter points amount"
            class="w-full"
            :class="{ 'p-invalid': awardPointsForm.pointsError }"
          />
          <small v-if="awardPointsForm.pointsError" class="p-error">
            {{ awardPointsForm.pointsError }}
          </small>
        </div>

        <div class="field">
          <label for="pointsReason" class="block text-900 font-medium mb-2">
            Reason <span class="text-red-500">*</span>
          </label>
          <Textarea
            id="pointsReason"
            v-model="awardPointsForm.reason"
            rows="3"
            placeholder="Enter reason for awarding points..."
            class="w-full"
            :class="{ 'p-invalid': awardPointsForm.reasonError }"
          />
          <small v-if="awardPointsForm.reasonError" class="p-error">
            {{ awardPointsForm.reasonError }}
          </small>
        </div>
      </div>

      <div class="flex justify-content-end gap-2">
        <Button
          label="Cancel"
          icon="pi pi-times"
          class="p-button-text"
          @click="cancelAwardPoints"
        />
        <Button
          label="Award Points"
          icon="pi pi-check"
          class="p-button-success"
          @click="awardPoints"
          :loading="awardingPoints"
          :disabled="!isAwardPointsFormValid"
        />
      </div>
    </div>
  </Dialog>

  <div class="page-container">
    <div class="max-w-7xl mx-auto p-6">
      <!-- Header Section -->
      <div class="bg-white border-round-2xl shadow-1 p-6 mb-6">
        <div class="flex align-items-center justify-content-between">
          <div class="flex align-items-center gap-4">
            <div class="header-icon-container">
              <i class="fa fa-user text-white text-2xl"></i>
            </div>
            <div>
              <h1 class="font-bold text-gray-800 text-3xl m-0 mb-2">
                {{ pageTitle }}
              </h1>
              <p class="text-gray-600 m-0 text-lg">{{ pageDescription }}</p>
            </div>
          </div>
          <Button
            v-if="hasPermission('np.backoffice.users.editdelete')"
            :disabled="!userUpdated"
            @click="updateEntity"
            :label="'Update ' + entityName"
            icon="pi pi-cloud-upload"
            class="update-button"
          />
        </div>
      </div>

      <Loading v-if="loading" :title="'Loading User'" :full="false" />
      <div class="text-center" v-if="!loading && !user">
        <h3>User not found</h3>
      </div>

      <div v-if="user">
        <Card class="mb-4 bg-white border-round-2xl shadow-1">
          <template #content>
            <div class="flex">
              <div class="flex flex-1 align-items-center">
                <div v-if="user.profilePhotoUrl">
                  <Avatar
                    :image="user.profilePhotoUrl"
                    class="mr-2"
                    size="xlarge"
                    shape="circle"
                  ></Avatar>
                </div>
                <div class="ml-2 flex align-items-center mr-3">
                  <!-- <div class="bg-blue-100 p-2 border-round-lg"> -->
                  <img
                    :src="
                      getAccountCreationSourceImage(user.accountCreationSource)
                    "
                    :alt="user.accountCreationSource || 'Unknown'"
                    class="account-source-icon"
                  />
                  <!-- </div> -->
                </div>
                <div class="flex flex-column justify-content-center">
                  <div class="font-semibold text-xl">
                    {{ user.firstName }} {{ user.lastName }}
                  </div>
                  <div class="mt-2">{{ user.email }}</div>
                  <div class="flex align-items-center mt-2" v-if="coinsAccount">
                    <span class=""
                      >Coins: <strong>{{ coinsAccount.balance }}</strong></span
                    >
                    <img
                      src="@/assets/nightcoins.svg"
                      style="height: 30px; width: auto"
                      alt="Coins"
                      class="icon ml-2"
                    />
                  </div>
                </div>
              </div>
              <div class="flex align-items-center">
                <Button
                  v-if="user.subscriptionProvider == 'BillWerk'"
                  @click="goToBillWerk"
                  label="BillWerk"
                  icon="pi pi-link"
                  class="p-button-secondary"
                />
                <Button
                  v-if="user.subscriptionProvider == 'Stripe'"
                  @click="goToStripe"
                  label="Stripe"
                  icon="pi pi-link"
                  class="p-button-secondary"
                />
                <Button
                  @click="goToAnalytics"
                  label="Analytics"
                  icon="pi pi-link"
                  class="ml-2 p-button-secondary"
                />
                <Button
                  @click="goToMixPanel"
                  label="Mixpanel"
                  icon="pi pi-link"
                  class="ml-2 p-button-secondary"
                />
              </div>
            </div>
          </template>
        </Card>

        <Card
          class="mb-3 bg-white border-round-2xl shadow-1 login-methods-card"
        >
          <template #header>
            <div class="flex align-items-center justify-content-between gap-3 px-4 pt-3">
              <div class="flex align-items-center gap-3">
                <div class="bg-teal-100 p-2 border-round-lg">
                  <i class="fa fa-sign-in-alt text-teal-600"></i>
                </div>
                <h4 class="m-0 font-semibold text-gray-800">Login methods</h4>
              </div>
              <span class="text-gray-500 text-sm">(data from Firebase Authentication)</span>
            </div>
          </template>
          <template #content>
            <div class="login-methods-content px-3 py-2 text-sm">
              <div v-if="firebaseAuthLoading" class="text-gray-500">
                Loading login methods...
              </div>
              <div v-else-if="firebaseAuthError" class="text-red-500">
                {{ firebaseAuthError }}
              </div>
              <div
                v-else-if="firebaseAuthInfo"
                class="flex align-items-center gap-4"
              >
                <div class="text-gray-800">
                  <span class="font-semibold">Last login:</span>
                  <span class="ml-1 text-900">
                    {{
                      formatFirebaseLastLogin(
                        firebaseAuthInfo.lastLoginAt ||
                          firebaseAuthInfo.lastLoginAtUtc ||
                          firebaseAuthInfo.lastLoginAtUTC
                      )
                    }}
                  </span>
                </div>
                <div
                  class="flex align-items-center gap-2 flex-wrap justify-content-center flex-1"
                >
                  <span class="font-semibold text-gray-800"
                    >Registered providers:</span
                  >
                  <template
                    v-if="
                      firebaseAuthInfo.providers &&
                      firebaseAuthInfo.providers.length
                    "
                  >
                    <div
                      v-for="provider in firebaseAuthInfo.providers"
                      :key="provider"
                      class="login-provider-pill-compact"
                    >
                      <img
                        v-if="getFirebaseProviderImage(provider)"
                        :src="getFirebaseProviderImage(provider)"
                        :alt="provider"
                        class="login-provider-icon-compact"
                      />
                      <i
                        v-else
                        :class="getFirebaseProviderIconClass(provider)"
                        class="text-xl"
                      ></i>
                    </div>
                  </template>
                  <span v-else class="text-gray-500"> None </span>
                </div>
              </div>
              <div v-else class="text-gray-500">
                No Firebase auth info available.
              </div>
            </div>
          </template>
        </Card>

        <!-- Tier Level and Points System Card -->
        <Card class="mb-4 bg-white border-round-2xl shadow-1">
          <template #header>
            <div
              class="flex justify-content-between align-items-center px-4 pt-3"
            >
              <div class="flex align-items-center gap-3">
                <div class="bg-blue-100 p-2 border-round-lg">
                  <i class="fa fa-trophy text-blue-600"></i>
                </div>
                <h4 class="m-0 font-semibold text-gray-800">
                  Level & Points System
                </h4>
              </div>
              <Button
                v-if="hasPermission('np.backoffice.users.editdelete')"
                @click="showAwardPointsDialog = true"
                label="Award Points"
                icon="pi pi-plus-circle"
                class="award-points-button"
                size="small"
              />
            </div>
          </template>
          <template #content>
            <!-- Current Period Stats -->
            <div class="mb-4">
              <h5 class="text-900 font-semibold mb-3">Current Period</h5>
              <div class="grid">
                <div class="col-12 md:col-6 lg:col-3">
                  <div class="bg-blue-50 border-round p-3 text-center">
                    <i class="fa fa-trophy text-blue-500 text-2xl mb-2"></i>
                    <div class="text-blue-600 font-semibold text-sm">
                      Active Level
                    </div>
                    <div class="text-blue-900 font-bold text-xl">
                      {{ user.activeLevel || 1 }}
                    </div>
                    <div class="text-blue-500 text-xs mt-1">Benefits Level</div>
                  </div>
                </div>
                <div class="col-12 md:col-6 lg:col-3">
                  <div class="bg-cyan-50 border-round p-3 text-center">
                    <i class="fa fa-trophy text-cyan-500 text-2xl mb-2"></i>
                    <div class="text-cyan-600 font-semibold text-sm">
                      Current period Level
                    </div>
                    <div class="text-cyan-900 font-bold text-xl">
                      {{ user.currentLevel || 1 }}
                    </div>
                    <div class="text-cyan-500 text-xs mt-1">
                      Based on Current period Points
                    </div>
                  </div>
                </div>
                <div class="col-12 md:col-6 lg:col-3">
                  <div class="bg-purple-50 border-round p-3 text-center">
                    <i class="fa fa-star text-purple-500 text-2xl mb-2"></i>
                    <div class="text-purple-600 font-semibold text-sm">
                      Current period Points
                    </div>
                    <div class="text-purple-900 font-bold text-xl">
                      {{ user.currentPoints || 0 }}
                    </div>
                    <div class="text-purple-500 text-xs mt-1">
                      Current Period
                    </div>
                  </div>
                </div>
                <div class="col-12 md:col-6 lg:col-3">
                  <div class="bg-indigo-50 border-round p-3 text-center">
                    <i class="pi pi-calendar text-indigo-500 text-2xl mb-2"></i>
                    <div class="text-indigo-600 font-semibold text-sm">
                      Last Reset
                    </div>
                    <div class="text-indigo-900 font-bold text-sm">
                      {{ formatSeasonResetDate(user.lastLevelResetDate) }}
                    </div>
                    <div class="text-indigo-500 text-xs mt-1">
                      Period reset Date
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Historical & Lifetime Stats -->
            <div>
              <h5 class="text-900 font-semibold mb-3">Historical & Lifetime</h5>
              <div class="grid">
                <div class="col-12 md:col-6 lg:col-4">
                  <div class="bg-orange-50 border-round p-3 text-center">
                    <i
                      class="fa fa-trophy text-orange-500 text-2xl mb-2 pr-2"
                    ></i>
                    <i class="pi pi-history text-orange-500 text-2xl mb-2"></i>
                    <div class="text-orange-600 font-semibold text-sm">
                      Level Achieved Before Last Reset
                    </div>
                    <div class="text-orange-900 font-bold text-xl">
                      {{ user.levelAchievedBeforeLastReset || 1 }}
                    </div>
                    <div class="text-orange-500 text-xs mt-1">
                      Highest Before Reset
                    </div>
                  </div>
                </div>
                <div class="col-12 md:col-6 lg:col-4">
                  <div class="bg-green-50 border-round p-3 text-center">
                    <i class="fa fa-star text-green-500 text-2xl mb-2 pr-2"></i>
                    <i
                      class="pi pi-chart-line text-green-500 text-2xl mb-2"
                    ></i>
                    <div class="text-green-600 font-semibold text-sm">
                      Total Lifetime Points
                    </div>
                    <div class="text-green-900 font-bold text-xl">
                      {{ user.totalLifetimePoints || 0 }}
                    </div>
                    <div class="text-green-500 text-xs mt-1">All Time</div>
                  </div>
                </div>
                <div class="col-12 md:col-6 lg:col-4">
                  <div class="bg-pink-50 border-round p-3 text-center">
                    <i
                      class="pi pi-info-circle text-pink-500 text-2xl mb-2"
                    ></i>
                    <div class="text-pink-600 font-semibold text-sm">
                      Level Logic
                    </div>
                    <div class="text-pink-900 font-bold text-sm">
                      {{ getLevelLogicText() }}
                    </div>
                    <div class="text-pink-500 text-xs mt-1">
                      Active vs Current
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </Card>

        <Card class="mb-4 bg-white border-round-2xl shadow-1">
          <template #header>
            <div class="flex align-items-center gap-3 px-4 pt-3">
              <div class="bg-indigo-100 p-2 border-round-lg">
                <i class="fa fa-user-edit text-indigo-600"></i>
              </div>
              <h4 class="m-0 font-semibold text-gray-800">User Information</h4>
            </div>
          </template>
          <template #content>
            <Form
              :fields="fields"
              :context="user"
              @onChange="entityUpdated"
              :hasAction="false"
            />
          </template>
        </Card>

        <Card class="mb-4 bg-white border-round-2xl shadow-1" v-if="phoneUsage">
          <template #header>
            <div class="flex align-items-center gap-3 px-4 pt-3">
              <div class="bg-red-100 p-2 border-round-lg">
                <i class="fa fa-shield-alt text-red-600"></i>
              </div>
              <h4 class="m-0 font-semibold text-gray-800">
                Anti fraud statistics for {{ fullPhoneNumber }}
              </h4>
            </div>
          </template>
          <template #content>
            <div class="grid">
              <div class="col-12 md:col-4">
                <div
                  class="anti-fraud-stat-card bg-blue-50 border-round-lg p-4 text-center h-full"
                >
                  <div
                    class="flex align-items-center justify-content-center mb-3"
                  >
                    <div class="bg-blue-500 p-2 border-round-lg">
                      <i class="fa fa-user-plus text-white text-xl"></i>
                    </div>
                  </div>
                  <div class="text-blue-600 font-semibold text-sm mb-2">
                    Users Created
                  </div>
                  <div class="text-blue-900 font-bold text-3xl mb-1">
                    {{ numberOfCreatedUsersWithPhoneNumber }}
                  </div>
                  <div class="text-blue-500 text-xs">
                    Total accounts with this number
                  </div>
                </div>
              </div>
              <div class="col-12 md:col-4">
                <div
                  class="anti-fraud-stat-card bg-orange-50 border-round-lg p-4 text-center h-full"
                >
                  <div
                    class="flex align-items-center justify-content-center mb-3"
                  >
                    <div class="bg-orange-500 p-2 border-round-lg">
                      <i class="fa fa-credit-card text-white text-xl"></i>
                    </div>
                  </div>
                  <div class="text-orange-600 font-semibold text-sm mb-2">
                    Subscription Signups
                  </div>
                  <div class="text-orange-900 font-bold text-3xl mb-1">
                    {{ numberOfMembershipSignupsWithPhoneNumber }}
                  </div>
                  <div class="text-orange-500 text-xs">
                    Paid subscriptions started
                  </div>
                </div>
              </div>
              <div class="col-12 md:col-4">
                <div
                  class="anti-fraud-stat-card bg-purple-50 border-round-lg p-4 text-center h-full"
                >
                  <div
                    class="flex align-items-center justify-content-center mb-3"
                  >
                    <div class="bg-purple-500 p-2 border-round-lg">
                      <i class="fa fa-gift text-white text-xl"></i>
                    </div>
                  </div>
                  <div class="text-purple-600 font-semibold text-sm mb-2">
                    Trial Awards
                  </div>
                  <div class="text-purple-900 font-bold text-3xl mb-1">
                    {{ numberOfTrialEndedWithPhoneNumber }}
                  </div>
                  <div class="text-purple-500 text-xs">Free trials granted</div>
                </div>
              </div>
            </div>
          </template>
        </Card>

        <Card class="p-2 flex-grow-1 mb-5 bg-white border-round-2xl shadow-1">
          <template #header>
            <div class="flex px-4 pt-3">
              <div class="flex align-items-center gap-3">
                <div class="bg-green-100 p-2 border-round-lg">
                  <i class="fa fa-credit-card text-green-600"></i>
                </div>
                <h4 class="m-0 font-semibold text-gray-800">OnlinePOS card</h4>
              </div>
            </div>
          </template>
          <template #content>
            <div class="formgrid grid">
              <div class="field col">
                <label class="font-medium text-sm">Number</label>
                <InputText
                  type="text"
                  v-model="onlinePosCard.cardNumber"
                  readonly
                />
              </div>
              <div class="field col">
                <label class="font-medium text-sm">Name</label>
                <InputText
                  type="text"
                  v-model="onlinePosCard.cardName"
                  readonly
                />
              </div>
              <div class="field col">
                <label class="font-medium text-sm">Group</label>
                <InputText
                  type="text"
                  v-model="onlinePosCard.cardGroup"
                  readonly
                />
              </div>
            </div>

            <div class="flex flex-wrap justify-content-left">
              <Button
                v-if="onlinePosCard.cardNumber"
                @click="checkCard"
                label="Check card"
                icon="pi pi-check"
                class="p-button-primary"
              />
              &nbsp;
              <Button
                v-if="onlinePosCard.cardNumber"
                @click="listCampaigns"
                label="List campaign values"
                icon="pi pi-list"
                class="p-button-primary"
              />&nbsp;
              <Button
                v-if="showCreate"
                @click="createCard"
                label="Create new card"
                icon="pi pi-file"
                class="p-button-danger"
              />
            </div>
          </template>
        </Card>

        <Card
          v-if="showCampaigns"
          class="p-2 flex-grow-1 mb-5 bg-white border-round-2xl shadow-1"
        >
          <template #header>
            <div class="flex px-4 pt-3">
              <div class="flex align-items-center gap-3">
                <div class="bg-purple-100 p-2 border-round-lg">
                  <i class="fa fa-bullhorn text-purple-600"></i>
                </div>
                <h4 class="m-0 font-semibold text-gray-800">
                  OnlinePoS Campaigns
                </h4>
              </div>
            </div>
          </template>
          <template #content>
            <DataTable :value="campaigns">
              <Column field="name" header="Campaign"></Column>
              <Column field="campaignId" header="Campaign Id"></Column>
              <Column field="points" header="Campaign Points"></Column>
            </DataTable>
          </template>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";

import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";
import { useRoute } from "vue-router";

import appleImage from "@/assets/login_options/apple.png";
import emailImage from "@/assets/login_options/email.png";
import googleImage from "@/assets/login_options/google.png";
import mpayImage from "@/assets/login_options/mpay.png";
import phoneImage from "@/assets/login_options/phone.png";
import * as apiGetUserFirebaseAuthInfo from "@/api/users/getUserFirebaseAuthInfo";

export default {
  inject: ["hasPermission"],
  components: {},
  setup() {
    const storeActionUpdateEntity = "users/updateUserInformation";
    const storeActionUpdatePhone = "users/updateUserPhone";
    const storeActionUpdateEmail = "users/updateUserEmail";
    const storeActionCheckCard = "users/checkCard";
    const storeGetterGetCard = "users/onlinePosCard";
    const storeActionCreateCard = "users/createOnlinePosCard";
    const storeActionGetCampaigns = "users/getCampaigns";
    const storeGetterGetCampaigns = "users/campaignInfo";
    const entityName = "User";
    const pageTitle = "General";
    const pageDescription = "General User information";

    const loading = ref(true);
    const updating = ref(false);

    const store = useStore();
    const toast = useToast();
    const route = useRoute();

    const user = ref();
    const coinsAccount = ref();
    const unUpdateUser = ref();
    const userUpdated = computed(() => {
      return (
        user.value?.firstName != unUpdateUser.value?.firstName ||
        user.value?.lastName != unUpdateUser.value?.lastName ||
        user.value?.gender != unUpdateUser.value?.gender ||
        user.value?.email != unUpdateUser.value?.email ||
        user.value?.phone?.replaceAll(" ", "") !=
          unUpdateUser.value?.phone?.replaceAll(" ", "")
      );
    });
    const userId = route.params.userId;

    const showUpdatingDialog = ref(false);
    const showUpdatePhoneDialog = ref(false);
    const showUpdateEmailDialog = ref(false);
    const showCampaigns = ref(false);
    const showCreate = ref(false);
    const showAwardPointsDialog = ref(false);
    const awardingPoints = ref(false);

    const firebaseAuthInfo = ref(null);
    const firebaseAuthLoading = ref(false);
    const firebaseAuthError = ref(null);

    const fullPhoneNumber = ref();
    const phoneUsage = ref();
    const numberOfCreatedUsersWithPhoneNumber = computed(
      () => phoneUsage.value?.userCreations?.length ?? 0
    );
    const numberOfMembershipSignupsWithPhoneNumber = computed(
      () => phoneUsage.value?.userMembershipSignups?.length ?? 0
    );
    const numberOfTrialEndedWithPhoneNumber = computed(
      () => phoneUsage.value?.userMembershipTrials?.length ?? 0
    );

    const campaigns = ref([]);

    // Award Points Form
    const awardPointsForm = ref({
      points: null,
      reason: "",
      pointsError: "",
      reasonError: "",
    });

    const isAwardPointsFormValid = computed(() => {
      return (
        awardPointsForm.value.points &&
        awardPointsForm.value.points > 0 &&
        awardPointsForm.value.reason &&
        awardPointsForm.value.reason.trim().length > 0
      );
    });

    const onlinePosCard = computed(() => {
      if (user.value.onlinePosCard != null) {
        const decoded = user.value.onlinePosCard;

        return {
          cardNumber: decoded.cardNumber,
          cardName: decoded.cardName,
          cardGroup: decoded.cardGroup,
        };
      }
      return {};
    });

    const fields = ref([
      {
        id: "id",
        type: "text-input",
        title: "User ID",
        config: {
          readonly: "true",
        },
      },
      {
        id: "created",
        type: "text-datetime",
        title: "Joined",
      },
      {
        id: "lastAppOpen",
        type: "text-datetime",
        title: "Last App Open",
      },
      {
        id: "firstName",
        type: "text-input",
        title: "First Name",
      },
      {
        id: "lastName",
        type: "text-input",
        title: "Last Name",
      },
      {
        id: "email",
        type: "text-input",
        title: "Email",
      },
      {
        id: "phone",
        type: "phone-input",
        title: "Phone",
      },
      {
        id: "gender",
        type: "dropdown",
        title: "Gender",
        config: {
          options: ["NoGender", "Male", "Female"],
        },
      },
      {
        id: "accountId",
        path: "accountId.id",
        type: "country",
        title: "Country",
      },
      {
        id: "city",
        type: "text-input",
        title: "City",
      },
      {
        id: "zipcode",
        type: "text-input",
        title: "Zip Code",
      },
      {
        id: "shareCode",
        type: "text-input",
        title: "Share Code",
        config: {
          readonly: "true",
        },
      },
      {
        id: "crmGuestId",
        path: "crmGuestId.id",
        type: "text-input",
        title: "CRM Guest ID",
        config: {
          readonly: "true",
        },
      },
      {
        id: "freeDaysInfo",
        type: "FreeDaysDisplay",
        title: "Free Days",
        config: {
          readonly: "true",
        },
      },
    ]);

    function validateAwardPointsForm() {
      awardPointsForm.value.pointsError = "";
      awardPointsForm.value.reasonError = "";

      if (!awardPointsForm.value.points || awardPointsForm.value.points <= 0) {
        awardPointsForm.value.pointsError =
          "Points amount is required and must be greater than 0";
      }

      if (
        !awardPointsForm.value.reason ||
        awardPointsForm.value.reason.trim().length === 0
      ) {
        awardPointsForm.value.reasonError = "Reason is required";
      }

      return (
        !awardPointsForm.value.pointsError && !awardPointsForm.value.reasonError
      );
    }

    function resetAwardPointsForm() {
      awardPointsForm.value = {
        points: null,
        reason: "",
        pointsError: "",
        reasonError: "",
      };
    }

    function cancelAwardPoints() {
      showAwardPointsDialog.value = false;
      resetAwardPointsForm();
    }

    async function awardPoints() {
      if (!validateAwardPointsForm()) {
        return;
      }

      awardingPoints.value = true;

      try {
        const requestObj = {
          pointsToAward: awardPointsForm.value.points,
          reason: awardPointsForm.value.reason.trim(),
        };

        await store.dispatch("users/awardPointsManually", {
          userId: userId,
          requestObj: requestObj,
        });

        // Wait a moment for the backend to process, then force refresh user data
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Force refresh user data from API to get updated points information
        await store.dispatch("users/getUser", { userId: userId });
        const updatedUser = store.getters["users/user"](userId);
        user.value = JSON.parse(JSON.stringify(updatedUser));
        unUpdateUser.value = JSON.parse(JSON.stringify(updatedUser));

        // Update phone number and reload phone usage if needed
        fullPhoneNumber.value =
          user.value?.phoneCountryCode + user.value?.phone;
        await getPhoneUsageAsync();

        toast.add({
          severity: "success",
          summary: "Points Awarded",
          detail: `Successfully awarded ${awardPointsForm.value.points} points to ${user.value.firstName} ${user.value.lastName}`,
          life: 5000,
        });

        showAwardPointsDialog.value = false;
        resetAwardPointsForm();
      } catch (err) {
        toast.add({
          severity: "error",
          summary: "Error Awarding Points",
          detail: "Could not award points: " + err,
          life: 5000,
        });
      } finally {
        awardingPoints.value = false;
      }
    }

    function goToStripe() {
      window.open(
        "https://dashboard.stripe.com/customers/" + user.value.stripeCustomerId,
        "_blank"
      );
    }

    function goToBillWerk() {
      window.open(
        "https://admin.billwerk.plus/#/rp/customers/customers/customer/" +
          user.value.billWerkCustomerHandle,
        "_blank"
      );
    }

    function goToAnalytics() {
      window.open(
        "https://console.firebase.google.com/u/0/project/rekom-nightpay-bdf26/overview",
        "_blank"
      );
    }

    function goToMixPanel() {
      window.open(
        "https://eu.mixpanel.com/project/2893120/view/3422448/app/profile#distinct_id=" +
          user.value.id
      );
    }

    async function createCard() {
      loading.value = true;
      try {
        await store.dispatch(storeActionCreateCard, { userId });
        const onlinePosCard = store.getters[storeGetterGetCard];

        if (onlinePosCard.cardDeleted) {
          toast.add({
            severity: "warn",
            summary: "Card deleted",
            detail:
              user.value.onlinePosCard.cardNumber + " is deleted in OnlinePOS!",
            life: 3000,
          });
          showCreate.value = true;
        } else {
          toast.add({
            severity: "success",
            summary: "New OnlinePOS card created",
            detail:
              user.value.onlinePosCard.cardNumber +
              " was created in OnlinePOS!",
            life: 3000,
          });
          user.value.onlinePosCard = onlinePosCard;
          showCreate.value = false;
        }
      } catch (err) {
        toast.add({
          severity: "error",
          summary: "Error checking card against OnlinePOS",
          detail: err,
          life: 3000,
        });
      }
      loading.value = false;
    }

    async function checkCard() {
      loading.value = true;
      try {
        await store.dispatch(storeActionCheckCard, { userId });
        const onlinePosCard = store.getters[storeGetterGetCard];

        if (onlinePosCard.cardDeleted) {
          toast.add({
            severity: "warn",
            summary: "Card deleted",
            detail:
              user.value.onlinePosCard.cardNumber + " is deleted in OnlinePOS!",
            life: 3000,
          });
          showCreate.value = true;
        } else {
          toast.add({
            severity: "success",
            summary: "Card found",
            detail:
              user.value.onlinePosCard.cardNumber + " was found in OnlinePOS!",
            life: 3000,
          });
          user.value.onlinePosCard = onlinePosCard;
          showCreate.value = false;
        }
      } catch (err) {
        toast.add({
          severity: "error",
          summary: "Error checking card against OnlinePOS",
          detail: err,
          life: 3000,
        });
      }
      loading.value = false;
    }

    async function listCampaigns() {
      loading.value = true;
      try {
        await store.dispatch(storeActionGetCampaigns, { userId });
        const campaignInfo = store.getters[storeGetterGetCampaigns];
        console.log("campaignInfo", campaignInfo);
        campaigns.value = campaignInfo.campaigns;

        if (campaignInfo.cardDeleted) {
          toast.add({
            severity: "warn",
            summary: "Card deleted",
            detail:
              user.value.onlinePosCard.cardNumber + " is deleted in OnlinePOS!",
            life: 3000,
          });
          showCreate.value = true;
        } else {
          toast.add({
            severity: "success",
            summary: "Campaign info loaded",
            detail:
              campaigns.value.length + " campaigns was found in OnlinePOS!",
            life: 3000,
          });
          showCampaigns.value = true;
          showCreate.value = false;
        }
      } catch (err) {
        toast.add({
          severity: "error",
          summary: "Error getting campaigns from OnlinePOS",
          detail: err,
          life: 3000,
        });
      }
      loading.value = false;
    }

    function entityUpdated(formContext) {
      user.value = { ...formContext };

      if (formContext?.phoneCountryCode == null)
        user.value.phoneCountryCode = "45";
    }

    async function updateGeneralInformation() {
      showUpdatingDialog.value = true;

      try {
        var userInfo = {
          firstName: user.value.firstName,
          lastName: user.value.lastName,
          gender: user.value.gender,
          city: user.value.city,
          zipCode: user.value.zipCode,
        };

        await store.dispatch(storeActionUpdateEntity, {
          userId: userId,
          user: userInfo,
        });
        toast.add({
          severity: "success",
          summary: "Success",
          detail: entityName + " updated",
          life: 3000,
        });
      } catch (err) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Could not update " + entityName + ": " + err,
          life: 3000,
        });
      }
      showUpdatingDialog.value = false;

      unUpdateUser.value = user.value;
    }

    function changesPending() {
      return (
        user.value.firstName != unUpdateUser.value.firstName ||
        user.value.lastName != unUpdateUser.value.lastName ||
        user.value.gender != unUpdateUser.value.gender ||
        user.value.email != unUpdateUser.value.email ||
        user.value.phone?.replaceAll(" ", "") !=
          unUpdateUser.value.phone?.replaceAll(" ", "") ||
        user.value.city != unUpdateUser.value.city ||
        user.value.zipCode != unUpdateUser.value.zipCode
      );
    }

    async function updatePhone() {
      showUpdatingDialog.value = true;
      showUpdatePhoneDialog.value = false;

      var phoneInfo = {
        phone: user.value.phone?.replaceAll(" ", ""),
        phoneCountryCode: user.value.phoneCountryCode,
      };

      await store
        .dispatch(storeActionUpdatePhone, {
          userId: userId,
          phoneInfo: phoneInfo,
        })
        .then(() => {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: entityName + " updated",
            life: 3000,
          });
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Could not update Phone number: " + err,
            life: 3000,
          });
        });
      showUpdatingDialog.value = false;

      fullPhoneNumber.value = user.value?.phoneCountryCode + user.value?.phone;
      unUpdateUser.value.phone = user.value.phone;
      updateEntity();
    }

    async function updateEmail() {
      showUpdatingDialog.value = true;
      showUpdateEmailDialog.value = false;

      var emailInfo = {
        email: user.value.email,
      };

      await store
        .dispatch(storeActionUpdateEmail, {
          userId: userId,
          emailInfo: emailInfo,
        })
        .then(() => {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: entityName + " updated",
            life: 3000,
          });
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Could not update email: " + err,
            life: 3000,
          });
          user.value.email = unUpdateUser.value.email;
        });

      showUpdatingDialog.value = false;

      unUpdateUser.value.email = user.value.email;
      updateEntity();
    }

    async function updateEntity() {
      if (!changesPending()) return;

      if (
        user.value.phone?.replaceAll(" ", "") !=
        unUpdateUser.value.phone?.replaceAll(" ", "")
      ) {
        showUpdatePhoneDialog.value = true;
        return;
      }

      if (user.value.email != unUpdateUser.value.email) {
        showUpdateEmailDialog.value = true;
        return;
      }

      updateGeneralInformation();
    }

    async function getPhoneUsageAsync() {
      var phoneCountryCode = user.value.phoneCountryCode;
      var phoneNumber = user.value.phone;

      if (phoneCountryCode == null || phoneNumber == null) return;

      await store
        .dispatch("phoneUsage/getPhoneUsage", { phoneCountryCode, phoneNumber })
        .catch((err) => {
          console.log(err);
        });

      phoneUsage.value = store.getters["phoneUsage/phoneUsage"](
        phoneCountryCode,
        phoneNumber
      );
    }

    function formatSeasonResetDate(dateValue) {
      if (!dateValue || dateValue === "0001-01-01T00:00:00Z") {
        return "Never";
      }
      try {
        return new Date(dateValue).toLocaleDateString();
      } catch {
        return "Invalid date";
      }
    }

    function getLevelLogicText() {
      if (!user.value) return "Loading...";

      const currentLevel = user.value.currentLevel || 1;
      const activeLevel = user.value.activeLevel || 1;
      const lastPeriodLevel = user.value.levelAchievedBeforeLastReset || 1;

      if (activeLevel > currentLevel) {
        return "Last period level higher than current period level";
      } else if (lastPeriodLevel > currentLevel) {
        return "Last period level higher than current period level";
      } else {
        return "Currently on highest level achieved";
      }
    }

    function getAccountCreationSourceImage(accountCreationSource) {
      if (!accountCreationSource) {
        return phoneImage;
      }

      const source = accountCreationSource.toLowerCase();
      switch (source) {
        case "mobilepay":
          return mpayImage;
        case "apple":
          return appleImage;
        case "google":
          return googleImage;
        case "email":
          return emailImage;
        default:
          return phoneImage;
      }
    }

    function getFirebaseProviderImage(provider) {
      if (!provider) return null;

      const p = provider.toLowerCase();

      switch (p) {
        case "email":
          return emailImage;
        case "mobilepay":
          return mpayImage;
        case "apple":
          return appleImage;
        case "google":
          return googleImage;
        case "phone":
          return phoneImage;
        default:
          return null;
      }
    }

    function getFirebaseProviderIconClass(provider) {
      const p = provider ? provider.toLowerCase() : "";

      switch (p) {
        case "email":
          return "fa fa-envelope text-blue-500";
        case "phone":
          return "fa fa-phone text-green-500";
        case "mobilepay":
          return "fa fa-mobile text-indigo-500";
        case "google":
          return "fa fa-google text-red-500";
        case "apple":
          return "fa fa-apple text-gray-900";
        default:
          return "fa fa-sign-in-alt text-gray-500";
      }
    }

    function formatFirebaseLastLogin(dateValue) {
      if (!dateValue) {
        return "Never";
      }

      try {
        return new Date(dateValue).toLocaleString();
      } catch {
        return "Invalid date";
      }
    }

    async function loadFirebaseAuthInfo() {
      if (!userId) {
        return;
      }

      firebaseAuthLoading.value = true;
      firebaseAuthError.value = null;

      try {
        firebaseAuthInfo.value = await apiGetUserFirebaseAuthInfo.get(userId);
      } catch (err) {
        console.error("Error loading Firebase auth info", err);
        firebaseAuthError.value = "Could not load login methods";
      } finally {
        firebaseAuthLoading.value = false;
      }
    }

    async function loadRequiredData() {
      loading.value = true;
      try {
        await store.dispatch("users/ensureUser", { userId: userId });
        // coinsAccount.value = await store.dispatch("users/getCoinsAccount", { userId: userId });
        const loadedUser = store.getters["users/user"](userId);
        // coinsAccount.value = store.getters["users/coinsAccount"];
        console.log("coinsAccount", coinsAccount.value);
        user.value = JSON.parse(JSON.stringify(loadedUser));
        unUpdateUser.value = JSON.parse(JSON.stringify(loadedUser));

        fullPhoneNumber.value =
          user.value?.phoneCountryCode + user.value?.phone;

        await getPhoneUsageAsync();

        showCreate.value = !user.value.onlinePosCard;
      } catch (err) {
        toast.add({
          severity: "error",
          summary: "Could not find user",
          detail: err,
          life: 3000,
        });
        loading.value = false;
      }

      loading.value = false;
    }

    onMounted(async () => {
      await loadRequiredData();
      loadFirebaseAuthInfo();
    });

    return {
      pageTitle,
      pageDescription,
      loading,
      updating,

      phoneUsage,
      numberOfTrialEndedWithPhoneNumber,
      numberOfCreatedUsersWithPhoneNumber,
      numberOfMembershipSignupsWithPhoneNumber,

      entityUpdated,
      updateEntity,
      updateEmail,
      updatePhone,
      userUpdated,

      user,
      onlinePosCard,
      coinsAccount,
      fullPhoneNumber,

      firebaseAuthInfo,
      firebaseAuthLoading,
      firebaseAuthError,

      fields,
      campaigns,

      entityName,

      showUpdatingDialog,
      showUpdatePhoneDialog,
      showUpdateEmailDialog,
      showCampaigns,
      showCreate,
      showAwardPointsDialog,
      awardingPoints,

      // Award Points
      awardPointsForm,
      isAwardPointsFormValid,
      awardPoints,
      cancelAwardPoints,

      checkCard,
      listCampaigns,
      createCard,

      goToStripe,
      goToBillWerk,
      goToAnalytics,
      goToMixPanel,
      formatSeasonResetDate,
      getLevelLogicText,
      getAccountCreationSourceImage,
      getFirebaseProviderImage,
      getFirebaseProviderIconClass,
      formatFirebaseLastLogin,
    };
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/* Fixed background that doesn't scroll */
.page-container {
  min-height: 100vh;
  background-attachment: fixed;
}

/* Header icon container */
.header-icon-container {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%);
  padding: 0.75rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Update button styling */
.update-button {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%) !important;
  border: none !important;
  padding: 0.75rem 1.5rem !important;
  border-radius: 0.75rem !important;
  font-weight: 600 !important;
  color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease !important;
}

.update-button:hover:not(:disabled) {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  transform: translateY(-1px) !important;
}

.update-button:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
}

/* Award points button styling */
.award-points-button {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  border: none !important;
  color: white !important;
  font-weight: 600 !important;
  border-radius: 0.5rem !important;
}

.award-points-button:hover {
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3) !important;
  transform: translateY(-1px) !important;
}

/* Avatar styling */
.vs-avatar img {
  height: auto;
  display: block;
}

.vs-avatar,
.vs-avatar img {
  border-radius: inherit;
  width: 100%;
  transition: all 0.25s ease;
}

.vs-avatar {
  background: #f4f7f8;
  color: #fff;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2c3e50;
  height: 100%;
  box-shadow: inset 0 0 0 0 rgb(0 0 0 / 5%);
}

.vs-avatar-content {
  border-radius: 50%;
  z-index: 1;
  width: 44px;
  height: 44px;
  position: relative;
  transition: all 0.25s ease;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Award points dialog styling */
.award-points-dialog {
  padding: 0.5rem 0;
}

.award-points-dialog .field {
  margin-bottom: 1rem;
}

.award-points-dialog .p-inputnumber,
.award-points-dialog .p-inputtextarea {
  transition: all 0.2s ease;
}

.award-points-dialog .p-inputnumber:focus,
.award-points-dialog .p-inputtextarea:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 0.2rem rgba(16, 185, 129, 0.2);
}

/* Stats card hover effects */
:deep(.p-card) {
  transition: all 0.3s ease;
}

:deep(.p-card:hover) {
  box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.12) !important;
  transform: translateY(-2px);
}

/* Modal button styling */
:deep(.p-button-secondary) {
  border-radius: 0.5rem !important;
  transition: all 0.2s ease !important;
}

:deep(.p-button-secondary:hover) {
  transform: translateY(-1px) !important;
}

/* Ensure proper shadow styling */
.shadow-1 {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

/* Modern dialog styling */
:deep(.modern-dialog .p-dialog-header) {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  border-radius: 0.75rem 0.75rem 0 0;
  padding: 1.5rem;
}

:deep(.modern-dialog .p-dialog-content) {
  background: white;
  border-radius: 0 0 0.75rem 0.75rem;
  padding: 0;
}

:deep(.modern-dialog .p-dialog) {
  border-radius: 0.75rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border: none;
}

/* Anti-fraud stat cards */
.anti-fraud-stat-card {
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.anti-fraud-stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(
    90deg,
    var(--card-accent-color, #3b82f6),
    var(--card-accent-color-light, #60a5fa)
  );
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.anti-fraud-stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 8px 10px -6px rgba(0, 0, 0, 0.1);
}

.anti-fraud-stat-card:hover::before {
  transform: scaleX(1);
}

/* Blue card accent */
.bg-blue-50 {
  --card-accent-color: #3b82f6;
  --card-accent-color-light: #60a5fa;
}

/* Orange card accent */
.bg-orange-50 {
  --card-accent-color: #f97316;
  --card-accent-color-light: #fb923c;
}

/* Purple card accent */
.bg-purple-50 {
  --card-accent-color: #a855f7;
  --card-accent-color-light: #c084fc;
}

/* Icon container animations */
.anti-fraud-stat-card .bg-blue-500,
.anti-fraud-stat-card .bg-orange-500,
.anti-fraud-stat-card .bg-purple-500 {
  transition: all 0.3s ease;
}

.anti-fraud-stat-card:hover .bg-blue-500,
.anti-fraud-stat-card:hover .bg-orange-500,
.anti-fraud-stat-card:hover .bg-purple-500 {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Number animation */
.anti-fraud-stat-card .text-3xl {
  transition: all 0.3s ease;
}

.anti-fraud-stat-card:hover .text-3xl {
  transform: scale(1.05);
}

/* Account creation source icon styling */
.account-source-icon {
  width: 2rem;
  height: 2rem;
  object-fit: contain;
}

.login-methods-card :deep(.p-card-body) {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.login-provider-pill-compact {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.125rem;
  margin-right: 0.125rem;
}

.login-provider-icon-compact {
  width: 1.75rem;
  height: 1.75rem;
  object-fit: contain;
}

/* Responsive improvements */
@media (max-width: 768px) {
  .max-w-7xl {
    max-width: 100%;
  }

  .p-6 {
    padding: 1rem;
  }

  :deep(.p-card-body) {
    padding: 1rem !important;
  }

  /* Stack anti-fraud cards vertically on mobile */
  .anti-fraud-stat-card {
    margin-bottom: 1rem;
  }

  .anti-fraud-stat-card:last-child {
    margin-bottom: 0;
  }
}
</style>
